import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Key, Loader2, AlertCircle, CheckCircle2, Sparkles, Search, X, RefreshCw, FileText, ScanSearch, ArrowRight, Maximize2, Copy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { analyzeDocument, synthesizeAnalyses, type AnalysisModelConfig, type AnalysisResult } from "../data/gapAnalysisService";
import { fetchTemplates, type ApiTemplate } from "../data/api";
import { parseLexicalBody } from "../data/lexicalParser";
import { useTranslation } from "../context/TranslationContext";
import { LawVoLogo } from "../components/LawVoLogo";
import { MarkdownView } from "../components/MarkdownView";
import { toast } from "sonner";
/**
 * Extract plain text from a Lexical body JSON string by joining all parsed segments.
 */
function extractPlainTextFromBody(body: string): string {
  try {
    const segments = parseLexicalBody(body);
    return segments.map(s => s.text).join("\n\n");
  } catch {
    return "";
  }
}
export function GapAnalysis() {
  const {
    openRouterApiKey,
    modelConfigs
  } = useTranslation();
  // Map the user's translation models to analysis reviewer configs.
  // The draftId ('A' | 'B' | 'C') maps directly to reviewerId.
  // Memoized so the reference is stable across renders.
  const analysisModels: AnalysisModelConfig[] = useMemo(() => modelConfigs.map(m => ({
    id: m.id,
    label: m.label,
    reviewerId: m.draftId
  })), [modelConfigs]);
  // For synthesis, always use the user's "A" model (their strongest pick).
  // If somehow missing, fall back to the first available model.
  const synthesisModel = useMemo(() => analysisModels.find(m => m.reviewerId === "A") || analysisModels[0], [analysisModels]);
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Analysis run state
  const [activeTemplate, setActiveTemplate] = useState<ApiTemplate | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyses, setAnalyses] = useState<AnalysisResult[]>([]);
  const [synthesis, setSynthesis] = useState<AnalysisResult | null>(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  // Maximized box modal state
  const [maximizedBox, setMaximizedBox] = useState<{
    title: string;
    subtitle: string;
    content: string;
    accentColor: string;
  } | null>(null);
  // Load templates on mount
  const loadTemplates = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchTemplates();
      // Filter to undeleted templates only
      const undeleted = data.filter(t => !t.deleted);
      setTemplates(undeleted);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadTemplates();
  }, []);
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(t => (t.title || "").toLowerCase().includes(q) || (t.name || "").toLowerCase().includes(q) || String(t.id).includes(q));
  }, [templates, searchQuery]);
  const handleAnalyzeTemplate = async (template: ApiTemplate) => {
    if (!openRouterApiKey) {
      toast.error("Please set your OpenRouter API key in Settings first.");
      return;
    }
    const documentText = extractPlainTextFromBody(template.body);
    if (!documentText.trim()) {
      toast.error("This template has no readable text content to analyze.");
      return;
    }
    setActiveTemplate(template);
    setIsAnalyzing(true);
    setAnalyses([]);
    setSynthesis(null);
    setIsSynthesizing(false);
    try {
      // Run 3 analyses in parallel using the user's translation models
      const analysisPromises = analysisModels.map(model => analyzeDocument(documentText, model, openRouterApiKey));
      const results = await Promise.all(analysisPromises);
      setAnalyses(results);
      const failedCount = results.filter(r => r.error).length;
      if (failedCount > 0) {
        toast.error(`${failedCount} reviewer(s) failed. See results below.`);
      }
      const successfulAnalyses = results.filter(r => !r.error);
      if (successfulAnalyses.length >= 2 && synthesisModel) {
        setIsSynthesizing(true);
        const synthesisResult = await synthesizeAnalyses(documentText, successfulAnalyses, openRouterApiKey, {
          id: synthesisModel.id,
          label: synthesisModel.label
        });
        setSynthesis(synthesisResult);
        setIsSynthesizing(false);
        if (synthesisResult.error) {
          toast.error("Synthesis failed.");
        } else {
          toast.success("Gap analysis complete.");
        }
      } else {
        toast.error("Not enough successful reviews to synthesize.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };
  const handleBackToList = () => {
    setActiveTemplate(null);
    setAnalyses([]);
    setSynthesis(null);
    setIsAnalyzing(false);
    setIsSynthesizing(false);
  };
  const getReviewerColor = (reviewerId: string) => {
    switch (reviewerId) {
      case "A":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          text: "text-blue-700",
          pill: "bg-blue-100"
        };
      case "B":
        return {
          bg: "bg-violet-50",
          border: "border-violet-200",
          text: "text-violet-700",
          pill: "bg-violet-100"
        };
      case "C":
        return {
          bg: "bg-teal-50",
          border: "border-teal-200",
          text: "text-teal-700",
          pill: "bg-teal-100"
        };
      default:
        return {
          bg: "bg-slate-50",
          border: "border-slate-200",
          text: "text-slate-700",
          pill: "bg-slate-100"
        };
    }
  };
  // === RESULTS VIEW ===
  if (activeTemplate) {
    return <div className="min-h-screen bg-slate-50 p-8" data-id="element-328">
        <div className="max-w-7xl mx-auto space-y-6" data-id="element-329">
          <button onClick={handleBackToList} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-330">
            <ArrowLeft className="w-4 h-4" data-id="element-331" />
            Back to Templates
          </button>

          {/* Header */}
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm" data-id="element-332">
            <div className="flex items-start gap-4" data-id="element-333">
              <div className="p-3 bg-purple-50 rounded-lg" data-id="element-334">
                <ScanSearch className="w-7 h-7 text-purple-600" data-id="element-335" />
              </div>
              <div className="flex-1" data-id="element-336">
                <div className="flex items-center gap-2 mb-1" data-id="element-337">
                  <span className="text-xs font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-150" data-id="element-338">
                    #{activeTemplate.id}
                  </span>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium uppercase" data-id="element-339">
                    {activeTemplate.locale}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-slate-900" data-id="element-340">
                  {activeTemplate.title || activeTemplate.name || `Template #${activeTemplate.id}`}
                </h1>
                <p className="text-slate-500 text-sm mt-1" data-id="element-341">
                  Multi-LLM gap analysis with synthesis
                </p>
              </div>
              {isAnalyzing && <div className="flex items-center gap-2 text-sm text-purple-600 font-medium" data-id="element-342">
                  <Loader2 className="w-4 h-4 animate-spin" data-id="element-343" />
                  Running 3 reviewers...
                </div>}
            </div>
          </div>

          {/* 3 Reviewer Columns */}
          <div className="grid md:grid-cols-3 gap-6" data-id="element-344">
            {analysisModels.map(model => {
            const result = analyses.find(a => a.reviewerId === model.reviewerId);
            const colors = getReviewerColor(model.reviewerId);
            const isLoadingCol = isAnalyzing && !result;
            const canMaximize = result && !result.error && result.content;
            return <div key={model.reviewerId} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-345">
                  <div className={`px-6 py-4 ${colors.bg} border-b ${colors.border}`} data-id="element-346">
                    <div className="flex items-center justify-between gap-2" data-id="element-347">
                      <h3 className="font-semibold text-slate-900" data-id="element-348">
                        Reviewer {model.reviewerId}
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-349">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${colors.pill} ${colors.text}`} data-id="element-350">
                          {model.label}
                        </span>
                        {canMaximize && <button onClick={() => setMaximizedBox({
                      title: `Reviewer ${model.reviewerId}`,
                      subtitle: model.label,
                      content: result.content,
                      accentColor: colors.text
                    })} className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-white/60 rounded transition-colors" title="Maximize" data-id="element-351">
                            <Maximize2 className="w-3.5 h-3.5" data-id="element-352" />
                          </button>}
                      </div>
                    </div>
                  </div>
                  <div className="p-6 max-h-[500px] overflow-y-auto flex-1" data-id="element-353">
                    {isLoadingCol ? <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400" data-id="element-354">
                        <Loader2 className="w-6 h-6 animate-spin mb-3" data-id="element-355" />
                        <p className="text-sm" data-id="element-356">Analyzing...</p>
                      </div> : result?.error ? <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-lg" data-id="element-357">
                        <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-358" />
                        <div data-id="element-359">
                          <p className="text-sm font-medium text-red-900 mb-1" data-id="element-360">
                            Failed
                          </p>
                          <p className="text-sm text-red-700" data-id="element-361">{result.error}</p>
                        </div>
                      </div> : result ? <MarkdownView content={result.content} className="text-sm" data-id="element-362" /> : <div className="text-sm text-slate-400 italic" data-id="element-363">
                        Waiting...
                      </div>}
                  </div>
                </div>;
          })}
          </div>

          {/* Synthesis Box */}
          <AnimatePresence data-id="element-364">
            {(isSynthesizing || synthesis) && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-xl border-2 border-emerald-200 shadow-lg overflow-hidden" data-id="element-365">
                <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100" data-id="element-366">
                  <div className="flex items-center justify-between gap-2" data-id="element-367">
                    <div className="flex items-center gap-2" data-id="element-368">
                      <Sparkles className="w-5 h-5 text-emerald-600" data-id="element-369" />
                      <h3 className="font-semibold text-slate-900" data-id="element-370">
                        Final Synthesis
                      </h3>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-371">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-id="element-372">
                        {synthesisModel?.label || "Synthesis"} · Synthesis
                      </span>
                      {synthesis && !synthesis.error && synthesis.content && <button onClick={() => setMaximizedBox({
                    title: "Final Synthesis",
                    subtitle: `${synthesisModel?.label || "Synthesis"}`,
                    content: synthesis.content,
                    accentColor: "text-emerald-700"
                  })} className="p-1.5 text-emerald-700 hover:text-emerald-900 hover:bg-white/60 rounded transition-colors" title="Maximize" data-id="element-373">
                          <Maximize2 className="w-3.5 h-3.5" data-id="element-374" />
                        </button>}
                    </div>
                  </div>
                </div>
                <div className="p-6 max-h-[600px] overflow-y-auto" data-id="element-375">
                  {isSynthesizing ? <div className="flex items-center gap-3 text-slate-500 py-8 justify-center" data-id="element-376">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-600" data-id="element-377" />
                      <span className="text-sm font-medium" data-id="element-378">
                        Synthesizing reviewer findings...
                      </span>
                    </div> : synthesis?.error ? <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-lg" data-id="element-379">
                      <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-380" />
                      <div data-id="element-381">
                        <p className="text-sm font-medium text-red-900 mb-1" data-id="element-382">
                          Synthesis failed
                        </p>
                        <p className="text-sm text-red-700" data-id="element-383">
                          {synthesis.error}
                        </p>
                      </div>
                    </div> : synthesis ? <MarkdownView content={synthesis.content} className="text-sm" data-id="element-384" /> : null}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Maximized Box Modal */}
        <AnimatePresence data-id="element-385">
          {maximizedBox && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setMaximizedBox(null)} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40" data-id="element-386" />

              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="fixed inset-4 md:inset-12 z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-387">
                <div className="flex items-center justify-between px-8 py-5 border-b border-slate-200 bg-white shrink-0" data-id="element-388">
                  <div data-id="element-389">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-390">
                      {maximizedBox.title}
                    </h2>
                    <p className={`text-sm font-medium mt-0.5 ${maximizedBox.accentColor}`} data-id="element-391">
                      {maximizedBox.subtitle}
                    </p>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-392">
                    <button onClick={() => {
                  navigator.clipboard.writeText(maximizedBox.content);
                  toast.success("Copied to clipboard");
                }} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Copy content" data-id="element-393">
                      <Copy className="w-4 h-4" data-id="element-394" />
                      Copy
                    </button>
                    <button onClick={() => setMaximizedBox(null)} className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" title="Close" data-id="element-395">
                      <X className="w-5 h-5" data-id="element-396" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-8 py-6" data-id="element-397">
                  <div className="max-w-3xl mx-auto" data-id="element-398">
                    <MarkdownView content={maximizedBox.content} className="text-base leading-relaxed" data-id="element-399" />
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>;
  }
  // === TEMPLATES LIST VIEW ===
  return <div className="min-h-screen bg-slate-50 p-8" data-id="element-400">
      <div className="max-w-5xl mx-auto space-y-6" data-id="element-401">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-402">
          <ArrowLeft className="w-4 h-4" data-id="element-403" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-404">
          <div data-id="element-405">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-406">
              <div className="p-2 bg-purple-600 text-white rounded-lg shadow-sm" data-id="element-407">
                <ScanSearch className="w-6 h-6" data-id="element-408" />
              </div>
              Legal Gap Analysis
            </h1>
            <p className="text-slate-500 mt-1" data-id="element-409">
              Run multi-LLM review on any template to identify legal gaps and
              risk exposure
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-410">
            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${openRouterApiKey ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-700"}`} data-id="element-411">
              <Key className="w-4 h-4" data-id="element-412" />
              {openRouterApiKey ? "API Key ✓" : "No API Key"}
            </div>
            <button onClick={loadTemplates} disabled={isLoading} className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50" data-id="element-413">
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} data-id="element-414" />
              Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative" data-id="element-415">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-416" />
          <input type="text" placeholder="Search templates by title, name, or ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-11 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all shadow-sm" data-id="element-417" />

          {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors" data-id="element-418">
              <X className="w-4 h-4" data-id="element-419" />
            </button>}
        </div>

        {/* Count */}
        {!isLoading && !loadError && templates.length > 0 && <div className="px-1 text-xs text-slate-400 font-medium" data-id="element-420">
            {filteredTemplates.length} template
            {filteredTemplates.length !== 1 ? "s" : ""}
            {searchQuery && ` matching "${searchQuery}"`}
          </div>}

        {/* Loading */}
        {isLoading && templates.length === 0 && <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-421">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-4" data-id="element-422" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-423">
              Loading templates...
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-424">Fetching from the server</p>
          </div>}

        {/* Error */}
        {loadError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4" data-id="element-425">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-426" />
            <div className="flex-1" data-id="element-427">
              <h3 className="font-semibold text-red-900 mb-1" data-id="element-428">
                Failed to load templates
              </h3>
              <p className="text-sm text-red-700 mb-3" data-id="element-429">{loadError}</p>
              <button onClick={loadTemplates} className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors" data-id="element-430">
                <RefreshCw className="w-4 h-4" data-id="element-431" />
                Try Again
              </button>
            </div>
          </div>}

        {/* List */}
        {!isLoading && !loadError && filteredTemplates.length > 0 && <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100" data-id="element-432">
            {filteredTemplates.map(template => <div key={template.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors group" data-id="element-433">
                <div className="shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600" data-id="element-434">
                  <FileText className="w-5 h-5" data-id="element-435" />
                </div>

                <div className="flex-1 min-w-0" data-id="element-436">
                  <div className="flex items-center gap-2" data-id="element-437">
                    <h3 className={`text-sm font-semibold truncate ${template.active ? "text-slate-900" : "text-slate-400"}`} data-id="element-438">
                      {template.title || template.name || `Template #${template.id}`}
                    </h3>
                    {!template.active && <span className="shrink-0 px-1.5 py-0.5 bg-slate-100 text-slate-400 text-[10px] font-medium rounded" data-id="element-439">
                        Inactive
                      </span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1" data-id="element-440">
                    <span className="text-[10px] font-mono text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-150" data-id="element-441">
                      #{template.id}
                    </span>
                    <span className="text-[10px] font-medium uppercase text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-442">
                      {template.locale}
                    </span>
                    {template.sectionName && <span className="text-xs text-slate-400 truncate" data-id="element-443">
                        {template.sectionName}
                      </span>}
                    <span className="text-xs text-slate-400" data-id="element-444">
                      {new Date(template.updated_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
                    </span>
                  </div>
                </div>

                <button onClick={() => handleAnalyzeTemplate(template)} disabled={!openRouterApiKey} className="shrink-0 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm" title={!openRouterApiKey ? "Set your API key in Settings first" : "Run gap analysis"} data-id="element-445">
                  <Sparkles className="w-4 h-4" data-id="element-446" />
                  Analyze
                  <ArrowRight className="w-4 h-4" data-id="element-447" />
                </button>
              </div>)}
          </div>}

        {/* Empty search results */}
        {!isLoading && !loadError && templates.length > 0 && filteredTemplates.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-id="element-448">
              <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" data-id="element-449" />
              <h3 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-450">
                No results
              </h3>
              <p className="text-sm text-slate-500" data-id="element-451">
                No templates match "{searchQuery}"
              </p>
            </div>}

        {/* Empty state */}
        {!isLoading && !loadError && templates.length === 0 && <div className="bg-white rounded-xl border border-slate-200 border-dashed p-12 text-center" data-id="element-452">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-453" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-454">
              No templates found
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-455">
              No undeleted templates returned from the server.
            </p>
          </div>}
      </div>
    </div>;
}